import React, { FC, useEffect, useState } from 'react';
import { BasicContainer, ImageButton, FlexRow } from '@minecraft.atoms';
import Icon from '@minecraft.icon';

export type CarouselImage = {
  src: string;
  alt?: string;
  id?: string;
};

export type ImageCarouselProps = {
  images: CarouselImage[];
  /** Auto-advance every three seconds. Opt-in so static galleries stay manual. */
  autoplay?: boolean;
  previousLabel: string;
  nextLabel: string;
  'data-testid'?: string;
  'data-qa-id'?: string;
};

const ImageCarouselArrow: FC<{ direction: 'previous' | 'next'; label: string; onClick: () => void }> = ({
  direction,
  label,
  onClick,
}) => (
  <BasicContainer
    className="cn_pointer-events-auto"
    bgColor="cardInverted"
    bgOpacity={0.75}
    textColor="inverted"
    borderRadius="full"
    zIndex={1}
    minWidth={2}
    maxWidth={2}
  >
    <ImageButton
      type="button"
      aria-label={label}
      onClick={onClick}
      noPadding
      unsetSizing
      className="cn_w-full cn_h-8 cn_atom_rounded-full"
    >
      <FlexRow justifyContent="center" alignItems="center" h="full" wrap="nowrap">
        <Icon name={direction === 'previous' ? 'back-2' : 'right-arrow'} size="sm" inheritColor />
      </FlexRow>
    </ImageButton>
  </BasicContainer>
);

/**
 * A rounded image gallery with arrows, indicators and optional autoplay.
 * Autoplay resets after manual navigation.
 */
export const ImageCarousel: FC<ImageCarouselProps> = ({
  images,
  autoplay = false,
  previousLabel,
  nextLabel,
  'data-testid': testId = 'image-carousel',
  'data-qa-id': qaId,
}) => {
  const [selectedSlide, setSelectedSlide] = useState(0);
  const imageCount = images.length;
  const activeSlide = imageCount ? selectedSlide % imageCount : 0;

  useEffect(() => {
    if (!autoplay || imageCount < 2) {
      return undefined;
    }

    const timer = setInterval(() => {
      setSelectedSlide((index) => (index + 1) % imageCount);
    }, 3000);

    return () => clearInterval(timer);
  }, [autoplay, imageCount, selectedSlide]);

  const moveSlide = (direction: number) => {
    setSelectedSlide((index) => (index + direction + imageCount) % imageCount);
  };

  if (!imageCount) {
    return null;
  }

  return (
    <BasicContainer
      className="cn_relative cn_overflow-hidden"
      w="full"
      borderRadius="lg"
      data-testid={testId}
      data-qa-id={qaId}
    >
      {images.map((image, index) => (
        <BasicContainer key={`${image.id ?? index}:${image.src}`} w="full" hidden={index !== activeSlide}>
          <img src={image.src} alt={image.alt} className="cn_block cn_w-full cn_h-auto cn_object-cover" />
        </BasicContainer>
      ))}
      {imageCount > 1 && (
        <>
          <FlexRow
            className="cn_absolute cn_pointer-events-none"
            top={0}
            left={0}
            w="full"
            h="full"
            justifyContent="space-between"
            alignItems="center"
            padding="2"
            wrap="nowrap"
          >
            <ImageCarouselArrow direction="previous" label={previousLabel} onClick={() => moveSlide(-1)} />
            <ImageCarouselArrow direction="next" label={nextLabel} onClick={() => moveSlide(1)} />
          </FlexRow>
          <FlexRow
            className="cn_absolute cn_bottom-0"
            w="full"
            justifyContent="center"
            alignItems="center"
            gap="0"
            wrap="nowrap"
          >
            {images.map((image, index) => (
              <BasicContainer key={image.id ?? image.src} className="cn_relative cn_w-nav-badge cn_h-6">
                <BasicContainer
                  className="cn_center-absolute cn_pointer-events-none"
                  minWidth={0.4}
                  minHeight={0.4}
                  borderRadius="full"
                  bgColor={activeSlide === index ? 'card' : 'carouselDotInactive'}
                  data-testid={`${testId}-dot-${index + 1}`}
                  data-active={activeSlide === index ? 'true' : 'false'}
                />
              </BasicContainer>
            ))}
          </FlexRow>
        </>
      )}
    </BasicContainer>
  );
};

export default ImageCarousel;
